import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { products, categories, siteImages } from '../data/mock';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Filter, X, ChevronDown, Grid3X3, List, Check, Baby } from 'lucide-react';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState('grid');
  const { addToCart } = useCart();

  const selectedCategory = searchParams.get('category') || '';

  useEffect(() => {
    document.title = 'Shop Baby Footies, Gowns, Blankets & Gift Sets | Tiny Wonders Pick';
  }, []);

  const filteredProducts = useMemo(() => {
    let result = [...products];

    if (selectedCategory) {
      result = result.filter(p => p.categorySlug === selectedCategory);
    }

    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'name':
        result.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        break;
    }

    return result;
  }, [selectedCategory, sortBy]);

  const handleCategoryChange = (categorySlug) => {
    if (categorySlug) {
      setSearchParams({ category: categorySlug });
    } else {
      setSearchParams({});
    }
  };

  const handleQuickAdd = (e, product) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
  };

  const activeCategory = categories.find(c => c.slug === selectedCategory);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* Banner Section */}
        <section className="keep-dark relative h-64 md:h-80 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={siteImages.shopBanner}
              alt="Baby essentials"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/30" />
          </div>
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-500/20 border border-athletic-500/40 rounded-full text-white text-xs font-semibold mb-4">
                <Baby className="w-3.5 h-3.5" />
                BABY ESSENTIALS
              </div>
              <h1 className="text-4xl md:text-5xl text-white font-bold mb-3" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>
                {activeCategory ? activeCategory.name : 'Shop All'}
              </h1>
              <p className="text-white/80 max-w-lg">
                {activeCategory ? activeCategory.description : 'Soft footies, dreamy gowns, snuggly blankets and keepsake gift sets for your little wonder'}
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col lg:flex-row gap-8">

            {/* Sidebar Filters */}
            <aside className={`lg:w-64 flex-shrink-0 ${showFilters ? 'block' : 'hidden lg:block'}`}>
              <div className="bg-dark-900 rounded-2xl p-6 border border-dark-800 sticky top-28">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-bold text-white text-lg" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700 }}>Categories</h2>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="lg:hidden text-dark-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-2">
                  <button
                    onClick={() => handleCategoryChange('')}
                    className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                      !selectedCategory
                        ? 'bg-athletic-500 text-white'
                        : 'text-dark-300 hover:bg-dark-800 hover:text-white'
                    }`}
                  >
                    All Products
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-between ${
                        selectedCategory === cat.slug
                          ? 'bg-athletic-500 text-white'
                          : 'text-dark-300 hover:bg-dark-800 hover:text-white'
                      }`}
                    >
                      <span>{cat.name}</span>
                      {selectedCategory === cat.slug && <Check className="w-4 h-4" />}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Products Grid */}
            <div className="flex-1">
              {/* Toolbar */}
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8 bg-dark-900 rounded-xl p-4 border border-dark-800">
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setShowFilters(true)}
                    className="lg:hidden flex items-center gap-2 px-4 py-2 bg-dark-800 rounded-lg text-dark-300 hover:text-white transition-colors"
                  >
                    <Filter className="w-4 h-4" />
                    Filters
                  </button>
                  <p className="text-dark-400 text-sm">
                    <span className="text-white font-semibold">{filteredProducts.length}</span> products
                  </p>
                </div>

                <div className="flex items-center gap-4">
                  {/* View Mode */}
                  <div className="hidden sm:flex items-center gap-1 bg-dark-800 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-athletic-500 text-white' : 'text-dark-400 hover:text-white'}`}
                    >
                      <Grid3X3 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-athletic-500 text-white' : 'text-dark-400 hover:text-white'}`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Sort Dropdown */}
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none bg-dark-800 border border-dark-700 rounded-lg px-4 py-2 pr-10 text-sm text-white focus:outline-none focus:border-athletic-500 cursor-pointer"
                    >
                      <option value="featured">Featured</option>
                      <option value="price-low">Price: Low to High</option>
                      <option value="price-high">Price: High to Low</option>
                      <option value="name">Name: A-Z</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* Products */}
              {filteredProducts.length === 0 ? (
                <div className="text-center py-20 bg-dark-900 rounded-2xl border border-dark-800">
                  <Baby className="w-16 h-16 text-dark-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No products found</h3>
                  <p className="text-dark-400 mb-6">Try adjusting your filters</p>
                  <button
                    onClick={() => handleCategoryChange('')}
                    className="px-6 py-3 bg-athletic-500 text-white font-semibold rounded-xl hover:bg-athletic-400 transition-colors"
                  >
                    View All Products
                  </button>
                </div>
              ) : (
                <div className={viewMode === 'grid'
                  ? 'grid sm:grid-cols-2 lg:grid-cols-3 gap-6'
                  : 'space-y-4'
                }>
                  {filteredProducts.map(product => (
                    viewMode === 'grid' ? (
                      // Grid Card
                      <Link
                        key={product.id}
                        to={`/product/${product.slug}`}
                        className="group bg-dark-900 rounded-2xl overflow-hidden border border-dark-800 hover:border-athletic-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-athletic-500/10"
                      >
                        <div className="aspect-square bg-dark-800 relative overflow-hidden">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {product.badge && (
                            <span className="absolute top-3 left-3 px-3 py-1 bg-athletic-500 text-white text-xs font-bold uppercase tracking-wider rounded-lg">
                              {product.badge}
                            </span>
                          )}
                          {/* Quick Add Button */}
                          <button
                            onClick={(e) => handleQuickAdd(e, product)}
                            className="absolute bottom-4 right-4 p-3 bg-athletic-500 text-white rounded-xl opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all shadow-lg hover:bg-athletic-400"
                          >
                            <ShoppingCart className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="p-5">
                          <p className="text-xs text-athletic-400 uppercase tracking-wider font-semibold mb-2">
                            {categories.find(c => c.id === product.categoryId)?.name}
                          </p>
                          <h3 className="font-semibold text-white mb-3 group-hover:text-athletic-400 transition-colors line-clamp-2 min-h-[48px]">
                            {product.name}
                          </h3>
                          <p className="text-dark-400 text-sm mb-4 line-clamp-2">
                            {product.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-bold text-white">
                              ${product.price.toFixed(2)}
                            </span>
                            <span className="text-xs font-semibold px-3 py-1.5 bg-athletic-500/10 text-athletic-400 rounded-lg border border-athletic-500/20">
                              In Stock
                            </span>
                          </div>
                        </div>
                      </Link>
                    ) : (
                      // List Card
                      <Link
                        key={product.id}
                        to={`/product/${product.slug}`}
                        className="group flex bg-dark-900 rounded-2xl overflow-hidden border border-dark-800 hover:border-athletic-500/50 transition-all"
                      >
                        <div className="w-48 h-48 flex-shrink-0 bg-dark-800 relative overflow-hidden">
                          <img
                            src={product.image}
                            alt={product.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          {product.badge && (
                            <span className="absolute top-2 left-2 px-2 py-0.5 bg-athletic-500 text-white text-xs font-bold uppercase rounded">
                              {product.badge}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 p-6 flex flex-col justify-between">
                          <div>
                            <p className="text-xs text-athletic-400 uppercase tracking-wider font-semibold mb-1">
                              {categories.find(c => c.id === product.categoryId)?.name}
                            </p>
                            <h3 className="font-semibold text-white text-lg mb-2 group-hover:text-athletic-400 transition-colors">
                              {product.name}
                            </h3>
                            <p className="text-dark-400 text-sm line-clamp-2">
                              {product.description}
                            </p>
                          </div>
                          <div className="flex items-center justify-between mt-4">
                            <span className="text-2xl font-bold text-white">
                              ${product.price.toFixed(2)}
                            </span>
                            <button
                              onClick={(e) => handleQuickAdd(e, product)}
                              className="flex items-center gap-2 px-4 py-2 bg-athletic-500 text-white font-semibold rounded-lg hover:bg-athletic-400 transition-colors"
                            >
                              <ShoppingCart className="w-4 h-4" />
                              Add to Cart
                            </button>
                          </div>
                        </div>
                      </Link>
                    )
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
